import {SessionState, SessionStateMachine} from './SessionStateMachine';

export interface OperationContext {
  signal: AbortSignal;
  stateMachine: SessionStateMachine;
}

export interface Operation<T = unknown> {
  id: string;
  type: string;
  execute: (context: OperationContext) => T | Promise<T>;
  predicate?: (state: SessionState) => boolean;
  onError?: (error: Error, context: OperationContext) => Promise<void> | void;
}

interface QueuedOperation<T = unknown> extends Operation<T> {
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  startTime: number;
}

/**
 * SessionOperationQueue ensures sequential execution of session operations
 * with proper cancellation support and state validation.
 *
 * Key guarantees:
 * - Operations execute sequentially (never parallel)
 * - Each operation receives an AbortSignal for cancellation
 * - Operations can validate they're still valid before execution
 * - Failed operations don't block the queue (configurable)
 */
export class SessionOperationQueue {
  private queue: QueuedOperation<any>[] = [];
  private processing = false;
  private currentAbortController: AbortController | null = null;
  private operationTimeout = 30000;

  constructor(private stateMachine: SessionStateMachine) {}

  /**
   * Enqueue an operation for sequential execution.
   * Returns a promise that resolves with the operation result.
   */
  async enqueue<T>(operation: Operation<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      const queuedOp: QueuedOperation<T> = {
        ...operation,
        resolve,
        reject,
        startTime: Date.now()
      };

      this.queue.push(queuedOp);
      this.processQueue();
    });
  }

  /**
   * Cancel all pending operations and abort current operation.
   */
  cancelAll(reason?: string): void {
    // Abort current operation
    if (this.currentAbortController) {
      this.currentAbortController.abort(reason || 'Operation queue cancelled');
      this.currentAbortController = null;
    }

    // Reject all pending operations
    const pending = this.queue.splice(0);
    for (const op of pending) {
      op.reject(new Error(`Operation cancelled: ${reason || 'queue cleared'}`));
    }
  }

  /**
   * Get number of pending operations.
   */
  get pendingCount(): number {
    return this.queue.length;
  }

  /**
   * Check if an operation is currently executing.
   */
  get isProcessing(): boolean {
    return this.processing;
  }

  private async processQueue(): Promise<void> {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;
    const operation = this.queue.shift()!;

    // Create abort controller for this operation
    this.currentAbortController = new AbortController();

    // Set up timeout
    const timeoutId = setTimeout(() => {
      this.currentAbortController?.abort(`Operation ${operation.id} timed out after ${this.operationTimeout}ms`);
    }, this.operationTimeout);

    try {
      // Check if operation is still valid
      if (operation.predicate) {
        const currentState = this.stateMachine.currentState;
        if (!operation.predicate(currentState)) {
          throw new Error(`Operation ${operation.id} predicate failed in state ${currentState.status}`);
        }
      }

      // Execute operation
      const context: OperationContext = {
        signal: this.currentAbortController.signal,
        stateMachine: this.stateMachine
      };

      const result = await operation.execute(context);
      operation.resolve(result);

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));

      // Call error handler if provided
      if (operation.onError) {
        try {
          await operation.onError(err, {
            signal: this.currentAbortController.signal,
            stateMachine: this.stateMachine
          });
        } catch (handlerError) {
          // Error handler failed, use original error
        }
      }

      operation.reject(err);

    } finally {
      clearTimeout(timeoutId);
      this.currentAbortController = null;
      this.processing = false;

      // Process next operation
      this.processQueue();
    }
  }
}

/**
 * Factory function to create an operation queue.
 */
export function createOperationQueue(stateMachine: SessionStateMachine): SessionOperationQueue {
  return new SessionOperationQueue(stateMachine);
}
